import React, { HTMLInputTypeAttribute, Ref, useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { HiX } from "react-icons/hi"
import { TbExclamationCircleFilled } from "react-icons/tb"
import { FormFieldProps, FormFieldTheme } from "src/components/admin/form/types"
import { ICON_BUTTON_LABELS } from 'src/utils/a11y.util';
import { twMerge } from "tailwind-merge"
import LocationFilter from 'components/HomeFilter/LocationFilter';
import { FieldValues, UseFormSetError } from 'react-hook-form';

type InputLocationFieldProps = FormFieldProps & {
   acceptChars?: RegExp
   clearable?: boolean
   description?: string
   disabled?: boolean
   endIcon?: React.ReactNode | React.ReactElement
   error?: string
   maxChars?: number
   name?: string
   placeholder?: string
   ref?: Ref<HTMLInputElement>
   startIcon?: React.ReactNode | React.ReactElement
   startIconLabel?: string
   endIconLabel?: string
   theme?: FormFieldTheme
   title?: string
   type?: HTMLInputTypeAttribute
   value?: string
   autoComplete?: string
   siteId?: string
   country?: string
   onStartIconClick?: (value?: string) => void
   onEndIconClick?: (value?: string) => void
   onChange?: (value?: string, center?: object, context?: object, mapboxId?: string, mapboxObject?: any) => void
   onClear?: () => void
   onFocus?: () => void
   onBlur?: () => void
   setError?: UseFormSetError<FieldValues>
}

const InputLocationField: React.FC<InputLocationFieldProps> = (
   props: InputLocationFieldProps
) => {
   const { acceptChars, clearable, description, disabled = false, error, maxChars, title, placeholder, endIcon, startIcon, startIconLabel, endIconLabel, theme, type, name, autoComplete, ref, value, onClear, onStartIconClick, onEndIconClick, onChange, onFocus, onBlur, setError, siteId, country } = props;
   const fieldId = useId();
   const errorId = `${fieldId}-error`;
   const descriptionId = `${fieldId}-description`;
   const ariaDescribedBy = [error ? errorId : null, description ? descriptionId : null].filter(Boolean).join(' ') || undefined;

   const [focus, setFocus] = useState(false);
   const [open, setOpen] = useState(false);
   const [searchText, setSearchText] = useState<string>('');
   const [locations, setLocations]: any = useState([]);
   const containerRef = useRef<HTMLDivElement>(null);
   const [dropdownStyle, setDropdownStyle] = useState({ top: 0, left: 0, width: 0 });

   // Document coordinates, not viewport ones: the dropdown is portaled to <body> (so admin
   // modals can't clip it) and positioned absolutely, so it scrolls with the page like the
   // input does. A position:fixed box only followed when a scroll/resize event fired, and on
   // mobile the keyboard opening and the browser scrolling the input into view don't reliably
   // fire them, which left the box sitting over the input (NPT-1959).
   const updateDropdownPosition = () => {
      if (containerRef.current) {
         const rect = containerRef.current.getBoundingClientRect();
         setDropdownStyle({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width });
      }
   };

   useEffect(() => {
      if (!open) return;
      updateDropdownPosition();
      const viewport = window.visualViewport;
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      viewport?.addEventListener('resize', updateDropdownPosition);
      return () => {
         window.removeEventListener('scroll', updateDropdownPosition, true);
         window.removeEventListener('resize', updateDropdownPosition);
         viewport?.removeEventListener('resize', updateDropdownPosition);
      };
   }, [open]);

   // Re-measure when suggestions arrive: the layout may have moved since focus.
   useEffect(() => {
      if (open && locations?.length > 0) updateDropdownPosition();
   }, [open, locations]);
   const leftSpace = `${onStartIconClick !== undefined || startIcon !== undefined ? 'pl-2' : 'pl-3.75'}`
   const rightSpace = `pr-2`
   const border = error ? 'border-twRed' : 'border-checkbox'
   const disabledBg = disabled === true ? 'bg-lightGray' : ''

   const handleSetLocation = ({ place_name, center, context, mapboxId, mapboxObject }: any) => {
      if (onChange) {
         onChange(place_name, center, context, mapboxId, mapboxObject);
      }
   }

   useEffect(() => {
      if (locations && searchText && locations.every((l: { name: string }) => {
         return l.name.trim().toLowerCase() !== searchText.trim().toLowerCase();
      })) {
         setError?.('address', { message: 'Please enter a valid street address' })
      }
      else if (!searchText) {
         setError?.('address', { message: 'Address is required' })
      } else {
         setError?.('address', {})
      }
   }, [searchText, locations, setError])

   return (
      <>
      <div className={theme?.container}>
         {/* Main input */}
         <div
            ref={containerRef}
            className={twMerge(`flex items-center border rounded-md pr-3.75 gap-x-2 h-full relative`, leftSpace, rightSpace, border, disabledBg, theme?.border)}
         >
            <label
               htmlFor={fieldId}
               className={`text-form__label absolute block bg-white -ml-1 px-1 transition ${value || focus ? '-top-2 text-xs' : 'text-sm'} text-twGray cursor-text`}
            >
               {title}
            </label>
            {startIcon !== undefined && (
               onStartIconClick !== undefined
                  ? <button aria-label={startIconLabel} className={twMerge("hover:bg-border rounded-md w-8.5 min-w-8.5 h-8.5 flex justify-center items-center", theme?.startIcon)}
                     onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        onStartIconClick?.(value)
                     }}
                  >
                     {startIcon}
                  </button>
                  : startIcon
            )}
            <input
               id={fieldId}
               className={twMerge(`bg-transparent border-none outline-none min-h-11.5 w-full text-sm placeholder-twGray`, theme?.input || '')}
               disabled={disabled}
               maxLength={maxChars}
               name={name}
               placeholder={(value || focus) ? placeholder : ''}
               ref={ref}
               type={type || "text"}
               value={value}
               autoComplete={autoComplete}
               aria-invalid={error !== undefined ? true : undefined}
               {...(ariaDescribedBy && { "aria-describedby": ariaDescribedBy })}
               onChange={(e) => {
                  setSearchText(e.target.value);
                  setOpen(true);
                  return onChange?.(e.target.value);
               }}
               onKeyDown={(e) => {
                  if (e.key === 'Escape' && open) {
                     e.stopPropagation();
                     setOpen(false);
                  }
               }}
               onBeforeInput={acceptChars
                  ? (e) => {
                     if (!acceptChars.test(e.data)) {
                        e.preventDefault();
                     }
                  }
                  : undefined
               }
               onFocus={() => {
                  setFocus(true);
                  updateDropdownPosition();
                  setTimeout(() => setOpen(true), 200);
                  onFocus?.();
               }}
               onBlur={() => {
                  setFocus(false);
                  setTimeout(() => setOpen(false), 200)
                  onBlur?.();
               }}
               onPaste={acceptChars
                  ? (e) => {
                     const pasted = e.clipboardData.getData('text');
                     if (!acceptChars.test(pasted)) {
                        e.preventDefault();
                     }
                  }
                  : undefined
               }
            />
            {error !== undefined && <TbExclamationCircleFilled size={18} color="#E02424" />}
            {clearable && <button
               type="button"
               aria-label={ICON_BUTTON_LABELS.CLEAR}
               className={`active:bg-gray-200 p-1 rounded-full ${value?.trim() !== '' ? 'visible' : 'invisible'}`}
               onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  onClear?.()
               }}
            >
               <HiX size={16} color='#686868' />
            </button>}
            {endIcon !== undefined && (
               <button
                  aria-label={endIconLabel}
                  className={twMerge("hover:enabled:bg-border rounded-md w-8.5 min-w-8.5 h-8.5 flex justify-center items-center", theme?.endIcon)}
                  disabled={onEndIconClick === undefined}
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()

                     onEndIconClick?.(value)
                  }}>
                  {endIcon}
               </button>
            )}
         </div>
         {/* Error text */}
         {error !== undefined && <p id={errorId} role="alert" className="m-0 text-twRed mt-2.5 text-xs">{error}</p>}
         {/* Description / Help text */}
         {description !== undefined && <div id={descriptionId} className="m-0 mt-2.5 text-twGray text-sm leading-4.5">{description}</div>}
      </div>
      {open && searchText?.length > 0 && typeof document !== 'undefined' && createPortal(
         <div
            style={{
               position: 'absolute',
               top: dropdownStyle.top,
               left: dropdownStyle.left,
               minWidth: Math.max(dropdownStyle.width, 316),
               zIndex: 9999,
            }}
            // LocationFilter stays mounted (it runs the suggest query) but the box is hidden
            // while there is nothing to pick, so free-typed addresses aren't shadowed by it.
            className={`bg-white py-2 shadow-md rounded-md overflow-hidden ${locations?.length > 0 ? '' : 'hidden'}`}
         >
            <LocationFilter {...{ setLocation: handleSetLocation, searchText, setLocations, locations, siteId, country }} className={theme?.wrapper} />
         </div>,
         document.body
      )}
   </>
   )
}

export default InputLocationField

